import { initializeApp } from 'firebase/app';
import { connectAuthEmulator, getAuth } from 'firebase/auth';
import { connectFirestoreEmulator, getFirestore } from 'firebase/firestore';
import { connectStorageEmulator, getStorage } from 'firebase/storage';
import { connectFunctionsEmulator, getFunctions } from 'firebase/functions';

const apiKey = import.meta.env.VITE_FIREBASE_API_KEY;
const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;
const authDomain = import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || 'primedocumentsign.firebaseapp.com';
const storageBucket = import.meta.env.VITE_FIREBASE_STORAGE_BUCKET;
const messagingSenderId = import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID;
const appId = import.meta.env.VITE_FIREBASE_APP_ID;
const measurementId = import.meta.env.VITE_FIREBASE_MEASUREMENT_ID;

if (!apiKey || !projectId || !appId) {
  throw new Error(
    'Firebase config missing. Add VITE_FIREBASE_API_KEY, VITE_FIREBASE_PROJECT_ID, and VITE_FIREBASE_APP_ID to your .env file. Get these from Firebase Console → Project settings.'
  );
}

const firebaseConfig = {
  apiKey,
  authDomain,
  projectId,
  storageBucket,
  messagingSenderId,
  appId,
  measurementId: measurementId || undefined,
};

export const app = initializeApp(firebaseConfig);

/** Defer Analytics SDK off the critical path (dynamic import + idle / delayed task). */
if (typeof window !== 'undefined' && measurementId) {
  const bootAnalytics = () => {
    import('firebase/analytics')
      .then(({ getAnalytics }) => {
        try {
          getAnalytics(app);
        } catch {
          /* ignore — e.g. blocked storage */
        }
      })
      .catch(() => {});
  };
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(bootAnalytics, { timeout: 4000 });
  } else {
    setTimeout(bootAnalytics, 2000);
  }
}

export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);
export const functions = getFunctions(app, 'us-central1');

/**
 * Local emulator wiring:
 * - Auto-enable when running on localhost/127.0.0.1
 * - Can be forced on/off with VITE_USE_FIREBASE_EMULATORS=true|false
 */
const localhostLike =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
const useEmulators = (() => {
  const raw = String(import.meta.env.VITE_USE_FIREBASE_EMULATORS || '').trim().toLowerCase();
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  return localhostLike;
})();

if (useEmulators) {
  const emuHost = import.meta.env.VITE_FIREBASE_EMULATOR_HOST || '127.0.0.1';
  const authPort = Number(import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_PORT || 9099);
  const firestorePort = Number(import.meta.env.VITE_FIREBASE_FIRESTORE_EMULATOR_PORT || 8080);
  const functionsPort = Number(import.meta.env.VITE_FIREBASE_FUNCTIONS_EMULATOR_PORT || 5001);
  const storagePort = Number(import.meta.env.VITE_FIREBASE_STORAGE_EMULATOR_PORT || 9199);

  try { connectAuthEmulator(auth, `http://${emuHost}:${authPort}`, { disableWarnings: true }); } catch { /* already connected */ }
  try { connectFirestoreEmulator(db, emuHost, firestorePort); } catch { /* already connected */ }
  try { connectFunctionsEmulator(functions, emuHost, functionsPort); } catch { /* already connected */ }
  try { connectStorageEmulator(storage, emuHost, storagePort); } catch { /* already connected */ }
}

/** GCP project id (for admin links to Cloud Console). */
export const firebaseProjectId = projectId;
